(() => {
  if (customElements.get('lumino-fbt')) return;

  class LuminoFbt extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.cards = [...this.querySelectorAll('[data-fbt-card]')];
      this.track = this.querySelector('[data-fbt-cards]');
      this.preview = this.querySelector('[data-fbt-preview]');
      this.toggle = this.querySelector('[data-fbt-toggle]');
      this.button = this.querySelector('[data-fbt-add]');
      this.notice = this.querySelector('[data-fbt-notice]');
      this.current = this.querySelector('[data-fbt-current]');
      this.page = document.querySelector(`product-page.main-product[data-id="${this.dataset.productId}"]`);
      this.mainForm = this.page?.querySelector('product-form form[data-type="add-to-cart-form"]');
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'ka', {
        style: 'currency', currency: this.dataset.currency
      });
      this.dataset.expanded = 'false';
      this.visibleCount = null;
      this.preview.replaceChildren();
      this.cards.forEach(card => {
        // Products sharing a template with required custom fields need their own form.
        if (card !== this.current && this.page?.querySelector('[data-js-custom-property] [required]')) {
          card.querySelector('[data-fbt-variant]').disabled = true;
          if (!card.querySelector('.lufy-fbt__option-link')) {
            const link = document.createElement('a');
            link.className = 'lufy-fbt__option-link';
            link.href = card.querySelector('.lufy-fbt__title').href;
            link.textContent = this.dataset.optionsLabel;
            card.querySelector('.lufy-fbt__details').append(link);
          }
        }
        const image = card.querySelector('.lufy-fbt__image img');
        const thumbnail = document.createElement('div');
        thumbnail.className = 'lufy-fbt__thumbnail';
        if (image) {
          const clone = image.cloneNode(false);
          clone.alt = '';
          thumbnail.append(clone);
        }
        this.preview.append(thumbnail);
        card.thumbnail = thumbnail;
      });
      this.addEventListener('change', event => {
        const card = event.target.closest('[data-fbt-card]');
        if (!card) return;
        if (event.target.matches('[data-fbt-variant]')) {
          this.updateVariant(card);
          const check = card.querySelector('[data-fbt-check]');
          check.checked = !check.disabled;
        }
        this.update();
      }, { signal });
      this.toggle.addEventListener('click', () => {
        this.dataset.expanded = String(this.dataset.expanded !== 'true');
        this.toggle.setAttribute('aria-expanded', this.dataset.expanded);
      }, { signal });
      this.button.addEventListener('click', () => this.add(), { signal });
      const sync = () => { this.syncCurrent(); this.update(); };
      document.addEventListener('input', event => {
        if (event.target.form === this.mainForm) sync();
      }, { signal });
      document.addEventListener('change', event => {
        if (event.target.form === this.mainForm) sync();
      }, { signal });
      this.page?.querySelectorAll('product-variants').forEach(picker => {
        picker.addEventListener('VARIANT_CHANGE', sync, { signal });
      });
      if (this.mainForm) {
        this.formObserver = new MutationObserver(sync);
        this.formObserver.observe(this.mainForm, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'disabled', 'value'] });
      }
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(this);
      this.cards.forEach(card => this.updateVariant(card));
      this.syncCurrent();
      this.resize();
      this.dataset.ready = 'true';
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.resizeObserver?.disconnect();
      this.formObserver?.disconnect();
    }

    resize() {
      const style = getComputedStyle(this);
      const gap = parseFloat(style.getPropertyValue('--fbt-gap'));
      const minimum = parseFloat(style.getPropertyValue('--fbt-item-min'));
      const side = parseFloat(style.getPropertyValue('--fbt-summary-width'));
      const available = this.getBoundingClientRect().width - side - (side ? 28 : 0);
      const capacity = Math.max(1, Math.floor((available + gap) / (minimum + gap)));
      const count = Math.min(this.cards.length, capacity);
      if (this.visibleCount === count) return;
      this.visibleCount = count;
      this.style.setProperty('--fbt-count', count);
      this.cards.forEach((card, index) => {
        card.hidden = index >= count;
        card.thumbnail.hidden = card.hidden;
      });
      this.update();
    }

    updateVariant(card) {
      const select = card.querySelector('[data-fbt-variant]');
      const option = select.selectedOptions[0];
      const check = card.querySelector('[data-fbt-check]');
      const wasDisabled = check.disabled;
      check.disabled = !select.value || option?.dataset.available !== 'true' || select.disabled;
      if (check.disabled) check.checked = false;
      else if (wasDisabled) check.checked = true;
      if (option?.dataset.priceHtml) card.querySelector('[data-fbt-price]').innerHTML = option.dataset.priceHtml;
      if (option?.dataset.image) {
        const image = card.querySelector('.lufy-fbt__image img');
        if (image) {
          image.src = option.dataset.image;
          image.removeAttribute('srcset');
          const thumbnail = card.thumbnail.querySelector('img');
          if (thumbnail) { thumbnail.src = image.src; thumbnail.removeAttribute('srcset'); }
        }
      }
    }

    syncCurrent() {
      if (!this.current) return;
      const select = this.current.querySelector('[data-fbt-variant]');
      const id = this.mainForm?.querySelector('[name="id"]')?.value;
      select.value = id || '';
      this.updateVariant(this.current);
      const check = this.current.querySelector('[data-fbt-check]');
      const submit = this.mainForm?.querySelector('[type="submit"]');
      if (!this.mainForm || submit?.classList.contains('disabled')) {
        check.disabled = true;
        check.checked = false;
      }
      const quantity = Number(new FormData(this.mainForm || document.createElement('form')).get('quantity') || 1);
      this.current.quantity = Number.isInteger(quantity) && quantity > 0 ? quantity : 1;
      const note = this.current.querySelector('[data-fbt-selection]');
      const variant = select.selectedOptions[0];
      const variantText = select.options.length > 1 ? variant?.textContent : '';
      note.textContent = [variantText, `${this.dataset.quantityLabel}: ${this.current.quantity}`].filter(Boolean).join(' · ');
    }

    selected() {
      return this.cards.filter(card => !card.hidden && !card.querySelector('[data-fbt-check]').disabled && card.querySelector('[data-fbt-check]').checked);
    }

    update() {
      const selected = this.selected();
      const total = selected.reduce((sum, card) => sum + Number(card.querySelector('[data-fbt-variant]').selectedOptions[0].dataset.price) * (card.quantity || 1), 0);
      const formatted = this.dataset.currency === 'GEL'
        ? `${this.money.formatToParts(total / 100).filter(part => part.type !== 'currency').map(part => part.value).join('').trim()} ₾`
        : this.money.format(total / 100);
      this.querySelectorAll('[data-fbt-total]').forEach(node => { node.textContent = formatted; });
      this.querySelector('[data-fbt-count-label]').textContent = this.dataset.countLabel.replace('[count]', selected.length);
      this.button.disabled = this.pending || selected.length === 0;
      this.cards.forEach(card => { card.dataset.selected = String(card.querySelector('[data-fbt-check]').checked); });
    }

    async add() {
      if (this.pending) return;
      this.syncCurrent();
      this.update();
      const selected = this.selected();
      if (!selected.length) return;
      const includesCurrent = selected.includes(this.current);
      if (includesCurrent && (!this.mainForm || !this.mainForm.reportValidity())) return;
      const body = new FormData();
      selected.forEach((card, index) => {
        if (card === this.current) {
          for (const [key, value] of new FormData(this.mainForm)) {
            if (['id', 'quantity', 'selling_plan'].includes(key)) body.append(`items[${index}][${key}]`, value);
            else if (/^properties\[.*\]$/.test(key)) body.append(`items[${index}][properties][${key.slice(11, -1)}]`, value);
          }
          if (!body.has(`items[${index}][quantity]`)) body.append(`items[${index}][quantity]`, '1');
        } else {
          body.append(`items[${index}][id]`, card.querySelector('[data-fbt-variant]').value);
          body.append(`items[${index}][quantity]`, '1');
        }
      });
      this.pending = true;
      this.button.setAttribute('aria-busy', 'true');
      this.notice.hidden = true;
      this.update();
      try {
        const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body });
        const result = await response.json();
        if (!response.ok || result.status >= 400) throw new Error('Cart request rejected');
        window.LuminoCartActions?.feedback(this.button);
        this.notice.textContent = this.dataset.successLabel;
        this.notice.dataset.state = 'success';
        this.notice.hidden = false;
        this.dispatchEvent(new CustomEvent('krown:cart:add', { bubbles: true, detail: { items: result.items } }));
      } catch {
        this.notice.replaceChildren(document.createTextNode(`${this.dataset.errorLabel} `));
        const link = document.createElement('a');
        link.href = this.dataset.cartUrl;
        link.textContent = this.dataset.cartLabel;
        this.notice.append(link);
        this.notice.dataset.state = 'error';
        this.notice.hidden = false;
      } finally {
        window.refreshCart?.(false);
        this.pending = false;
        this.button.removeAttribute('aria-busy');
        this.update();
      }
    }
  }
  customElements.define('lumino-fbt', LuminoFbt);
})();
